import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import api, { proxyImage } from '../services/api';

interface Video {
  id: number;
  bvid: string;
  aid: number;
  title: string;
  description: string;
  duration: number;
  pubdate: number;
  pic: string;
  subtitle_status: string;
}

interface Props {
  upmasterId: number;
}

interface VideoPageData {
  videos: Video[];
  pagination: { total: number; page: number; page_size: number; total_pages: number };
}

const VideoList: React.FC<Props> = ({ upmasterId }) => {
  const [crawlLoading, setCrawlLoading] = useState(false);
  // page=-1 表示"最新一页"（保持原行为：进入 upmaster / 切换每页数量时跳到最新页）
  const [pageSize, setPageSize] = useState(20);
  const [page, setPage] = useState(-1);

  // SWR: 视频分页（key 含 upmasterId/page/pageSize；keepPreviousData 让分页切换不闪加载态）
  const { data, isValidating, error, mutate } = useSWR<VideoPageData>(
    upmasterId ? ['videos', upmasterId, page, pageSize] : null,
    () => api.getVideos(upmasterId, page, pageSize).then(r => ({
      videos: r.data.data || [],
      pagination: r.data.pagination || {},
    })),
    { keepPreviousData: true }
  );

  const videos = data?.videos || [];
  const total = data?.pagination?.total || 0;
  const totalPages = data?.pagination?.total_pages || 0;
  // -1 模式下服务器返回真实页码后同步（避免 0 页等非法页码）
  const currentPage = page >= 1 ? page : (data?.pagination?.page || 1);

  // 服务器返回真实页码后同步（仅 -1 模式；校验 page_size 避免拿到旧数据的分页信息）
  useEffect(() => {
    if (page !== -1) return;
    const p = data?.pagination;
    if (p?.page && p.page_size === pageSize && p.page !== page) setPage(p.page);
  }, [data, page, pageSize]);

  const handleCrawl = async () => {
    setCrawlLoading(true);
    try {
      await api.startCrawl(upmasterId);
      alert('爬取任务已启动，请稍后刷新查看');
      setTimeout(() => mutate(), 5000);
    } catch (e) {
      console.error(e);
    } finally {
      setCrawlLoading(false);
    }
  };

  const openVideo = (bvid: string) => {
    window.open(`https://www.bilibili.com/video/${bvid}`, '_blank');
  };

  const formatDuration = (seconds: number) => {
    if (!seconds) return '--:--';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('zh-CN');
  };

  const completedCount = videos.filter(v => v.subtitle_status === 'completed').length;
  const noSubtitleCount = videos.filter(v => v.subtitle_status === 'no_subtitle').length;

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages: (number | 'ellipsis')[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('ellipsis');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (currentPage < totalPages - 2) pages.push('ellipsis');
      pages.push(totalPages);
    }

    return (
      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
        <button
          onClick={() => setPage(currentPage - 1)}
          disabled={currentPage <= 1}
          className="btn btn-ghost btn-sm"
          style={{ minWidth: '32px' }}
        >
          ‹
        </button>

        {pages.map((p, idx) =>
          p === 'ellipsis' ? (
            <span key={`ellipsis-${idx}`} style={{ padding: '0 8px', color: '#888' }}>…</span>
          ) : (
            <button
              key={p}
              onClick={() => setPage(p as number)}
              className={`btn btn-sm ${p === currentPage ? 'btn-brand' : 'btn-ghost'}`}
              style={{ minWidth: '32px' }}
            >
              {p}
            </button>
          )
        )}

        <button
          onClick={() => setPage(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="btn btn-ghost btn-sm"
          style={{ minWidth: '32px' }}
        >
          ›
        </button>
      </div>
    );
  };

  const pageSizes = [10, 20, 50, 100];

  return (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '12px' }}>
        <h3 style={{ margin: 0, fontSize: '18px' }}>视频列表 ({total})</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => mutate()} disabled={isValidating} className="btn btn-secondary btn-sm">
            🔄 刷新
          </button>
          <button onClick={handleCrawl} disabled={crawlLoading} className="btn btn-secondary btn-sm">
            {crawlLoading ? '⏳ 爬取中...' : '📥 开始爬取'}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '16px' }}>
        <div className="stat-item">
          <div className="stat-value">{completedCount}</div>
          <div className="stat-label">已完成</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{noSubtitleCount}</div>
          <div className="stat-label">无字幕</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">{total}</div>
          <div className="stat-label">总计</div>
        </div>
      </div>

      {/* Page Size */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <span style={{ fontSize: '13px', color: '#666' }}>每页</span>
          {pageSizes.map(size => (
            <button
              key={size}
              onClick={() => { setPageSize(size); setPage(-1); }}
              className={`btn btn-sm ${pageSize === size ? 'btn-brand' : 'btn-ghost'}`}
              style={{ minWidth: '40px' }}
            >
              {size}
            </button>
          ))}
        </div>
        {renderPagination()}
      </div>

      {/* Video List */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {data === undefined && !error ? (
          <div style={{ padding: '48px', textAlign: 'center' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            <p style={{ marginTop: '12px', color: '#666' }}>加载中...</p>
          </div>
        ) : error && data === undefined ? (
          <div className="empty-state">
            <div className="empty-state-icon">⚠️</div>
            <h3 className="empty-state-title">加载失败</h3>
            <p className="empty-state-desc">请检查网络后点击「刷新」重试</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">📹</div>
            <h3 className="empty-state-title">暂无视频</h3>
            <p className="empty-state-desc">点击「开始爬取」获取视频列表</p>
          </div>
        ) : (
          videos.map((video, index) => (
            <div 
              key={video.bvid} 
              className="video-card"
              style={{ 
                borderBottom: index < videos.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
              }}
            >
              <img
                loading="lazy"
                src={proxyImage(video.pic || '', 160, 90)}
                alt={video.title}
                className="video-thumbnail"
                onClick={() => openVideo(video.bvid)}
                style={{ cursor: 'pointer' }}
              />
              <div className="video-info">
                <h4 
                  className="video-title"
                  onClick={() => openVideo(video.bvid)}
                  style={{ cursor: 'pointer' }}
                >
                  {video.title}
                </h4>
                <div className="video-meta">
                  <span>⏱ {formatDuration(video.duration)}</span>
                  <span>📅 {formatDate(video.pubdate)}</span>
                </div>
                <div style={{ marginTop: '4px' }}>
                  <span className={`badge ${video.subtitle_status === 'completed' ? 'badge-success' : video.subtitle_status === 'no_subtitle' ? 'badge-warning' : video.subtitle_status === 'transcribe_failed' ? 'badge-danger' : video.subtitle_status === 'permanent_failed' ? 'badge-warning' : 'badge-muted'}`}>
                    {video.subtitle_status === 'completed' ? '✓ 有字幕' : 
                     video.subtitle_status === 'no_subtitle' ? '✗ 无字幕' :
                     video.subtitle_status === 'transcribe_failed' ? '✗ 转录失败' :
                     video.subtitle_status === 'permanent_failed' ? '⛔ 付费/不可转录' :
                     video.subtitle_status === 'transcribing' ? '⏳ 转录中' : '⏳ 待爬取'}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default VideoList;
